import * as React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { Activity } from '@aimmo/contracts';
import {
  Button,
  defaultListState,
  LIST_STATE_NAVIGATION,
  type ListState,
  PageHeader,
  ResourceList,
  type RowAction,
} from '@aimmo/ui';
import { CalendarPage } from '../features/calendar.js';
import { ActivityDialog } from '../features/activity-dialog.js';
import { useAbility } from '../lib/abilities.js';
import { isEditableActivity, removeActivity } from '../lib/activities.js';
import { remindersResource } from '../resources/reminders.js';

/**
 * Agenda: the month of appointments and the open reminders, side by side.
 *
 * They are one screen rather than two because they answer the same question —
 * what does today ask of me — and splitting them would make the answer take two
 * clicks (docs/domain/08-activities.md).
 */
export function AgendaPage() {
  // `strict: false` for the same reason the kit's own list routes do it: the
  // router cannot type a search shape it only learns at call time, and
  // `listSearchSchema` has already validated it.
  const search: Partial<ListState> = useSearch({ strict: false });
  const navigate = useNavigate();
  const [creating, setCreating] = React.useState<{ date?: string } | null>(null);
  const [editing, setEditing] = React.useState<Activity | null>(null);
  const ability = useAbility();
  const canUpdate = ability.can('update', 'Activity');
  const canDelete = ability.can('delete', 'Activity');

  // The worklist's own actions plus the two that need this page: editing opens
  // the dialog it owns, and both are offered only to whoever the API allows.
  const reminders = React.useMemo(() => {
    const extra: RowAction<Activity>[] = [];
    if (canUpdate) {
      extra.push({
        id: 'edit',
        label: 'Modifica',
        icon: <Pencil />,
        visible: isEditableActivity,
        onSelect: (row) => setEditing(row),
      });
    }
    if (canDelete) {
      extra.push({
        id: 'remove',
        label: 'Elimina',
        icon: <Trash2 />,
        tone: 'danger',
        visible: isEditableActivity,
        confirm: {
          title: 'Eliminare questo promemoria?',
          description: 'Sparisce dall’agenda e dalla cronologia. L’operazione non si annulla.',
          confirmLabel: 'Elimina',
        },
        onSelect: (row) => removeActivity(row.id),
      });
    }
    return {
      ...remindersResource,
      rowActions: [...(remindersResource.rowActions ?? []), ...extra],
    };
  }, [canUpdate, canDelete]);

  const state = React.useMemo<ListState>(
    () => ({ ...defaultListState(remindersResource.defaultState), ...search }),
    [search],
  );

  return (
    <div className="grid gap-[var(--card-gap)]">
      <PageHeader
        title="Agenda"
        description="Appuntamenti del mese e promemoria aperti."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreating({})}>
            <Plus />
            Nuovo evento
          </Button>
        }
      />

      {/* Clicking a day opens the same dialog with that date already in it. */}
      <CalendarPage onPickDay={(date) => setCreating({ date })} />

      <ResourceList
        resource={reminders}
        state={state}
        onStateChange={(next) => {
          // `next` is already the whole next state — the list applies its own
          // transitions. Re-applying them here produced a different object every
          // time, and the URL and the list chased each other in a loop.
          void navigate({ to: '/agenda', search: next, ...LIST_STATE_NAVIGATION });
        }}
      />

      {editing && (
        <ActivityDialog
          open
          activity={editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      )}

      {creating && (
        <ActivityDialog
          open
          defaultScheduledAt={creating.date}
          onOpenChange={(open) => {
            if (!open) setCreating(null);
          }}
        />
      )}
    </div>
  );
}
